import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import AppStoreButtons from './AppStoreButtons';
import assets from '../assets';

const Hero: React.FC = () => {
  const { t } = useLanguage();

  const handlePlayNowClick = () => {
    // Track the click
    if ((window as any).gtag) {
      (window as any).gtag('event', 'click_play_now', {
        event_category: 'engagement',
        event_label: 'play_now_button',
        value: 1,
      });
    }
    // Scroll to download section or open app store
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      downloadSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback: open app store
      window.open('https://apps.apple.com/app/spin-the-truth/id6757371160', '_blank');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-16 sm:pt-12 md:pt-20 p-8 sm:p-4 relative overflow-hidden">
      {/* Background image with people - matching the reference images */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${assets.heroPeople})`,
        }}
      >
        {/* Warm gradient overlay to enhance the party atmosphere */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/60 via-pink-900/50 to-orange-900/60"></div>
        <div className="absolute inset-0 bg-black/20"></div>
      </div>
      
      {/* Content Container - matching the image layout */}
      <div className="relative z-10 w-full max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start justify-between gap-12 lg:gap-16">
          
          {/* Left: Text Content */}
          <div className="flex-1 flex flex-col items-start text-left lg:max-w-lg z-20">
            {/* Headline */}
            <h1 className="text-white font-bold font-IBMPlex text-5xl sm:text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight drop-shadow-2xl">
              {t.hero.headline}
            </h1>
            
            {/* Subtext */}
            <p className="text-white/95 font-IBMPlex text-xl sm:text-lg md:text-2xl mb-8 drop-shadow-lg">
              {t.hero.subtext}
            </p>
            
            {/* CTA Button */}
            <button
              onClick={handlePlayNowClick}
              className="bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 hover:from-pink-600 hover:via-purple-600 hover:to-pink-700 text-white font-semibold font-IBMPlex text-lg sm:text-base md:text-xl px-8 py-4 rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-105 mb-8 flex items-center gap-2"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              {t.hero.ctaButton}
            </button>
            
            {/* Social Proof */}
            <div className="flex flex-wrap items-center gap-6 sm:gap-4 mb-8">
              <div className="flex items-center gap-2 text-white drop-shadow-lg">
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span className="font-IBMPlex font-medium text-sm sm:text-base">{t.hero.rating}</span>
              </div>
              <div className="flex items-center gap-2 text-white drop-shadow-lg">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                <span className="font-IBMPlex font-medium text-sm sm:text-base">{t.hero.players}</span>
              </div>
              <div className="flex items-center gap-2 text-white drop-shadow-lg">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span className="font-IBMPlex font-medium text-sm sm:text-base">{t.hero.partyFavorite}</span>
              </div>
            </div>
            
            {/* App Store Buttons */}
            <AppStoreButtons />
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default Hero;
